/*******************************************************************************
  DownloadCumulative.js

  Author: Mark Duckworth; Advait; 

*******************************************************************************/

import React, { useState, useRef } from "react";
import { downloadFile } from "../apihelpers/apihelpers";
import { showNotification } from "./helper/notification";
import { validateDates } from "./helper/timeUtils";
import HeadingSubText from "./HeadingSubText";
import "../index.css";

/**
 * Component to download a cumulative report of collected data.
 * Text fields for entering the start and end dates, and a download button.
 *
 * TODO This could be made more generic and combined with
 * TODO DownloadRoundData and DownloadUsageReports.
 */
function DownloadCumulativeReport(props) {
  const [areDatesValid, setAreDatesValid] = useState(false);
  // references to the input elements where the user types a date
  const startDateRef = useRef();
  const endDateRef = useRef();

  /**
   * Handler to check if both dates are entered, valid, and start date ≤ end date
   */
  function handleDateChange() {
    const startDate = startDateRef.current.value;
    const endDate = endDateRef.current.value;

    // For monitoring report, check 31-day range
    setAreDatesValid(
      validateDates(startDate, endDate, props.roundType === "monitoring" ? true : false, 31)
    );
  }

  /**
   * Click handler to perform the file download
   * @param {*} event
   */
  function doDownload(event) {
    event.preventDefault();
    const startDate = startDateRef.current.value;
    const endDate = endDateRef.current.value;
    showNotification(true, "Generating report...", true);
    // api path and filename prefix for inspection round data
    let apiPath = `/sites/${props.siteid}/inspections/cumulative?start=${startDate}&end=${endDate}`;
    let prefix = "cumulative";
    if (props.roundType === "monitoring") {
      // for monitoring data, which is different than inspection rounds
      apiPath = `/sites/${props.siteid}/monitoring_data?start=${startDate}&end=${endDate}`;
      prefix = "monitoring";
    }

    // Create a filename for the saved file on the local machine
    let filename = props.sysAdmin
      ? `${prefix}_${props.tenantName}_${props.siteName}_${startDate}_to_${endDate}.csv`
      : `${prefix}_${props.siteName}_${startDate}_to_${endDate}.csv`;
    downloadFile(apiPath, filename);
    // Reset the date fields after download
    startDateRef.current.value = "";
    endDateRef.current.value = "";
    setAreDatesValid(false);
  }

  return (
    <div className="individualContainer">
      <table className="individualContainerTable">
        <tbody>
          <tr>
            <td className="individualContainerTitleColumn" valign="top">
              <h4 className="individualContainerTitleHeading">
                {props.roundType === "inspections"
                  ? "Download Cumulative Inspection Report"
                  : "Download Monitoring Report"}
              </h4>
              {props.roundType === "monitoring" && (
                <HeadingSubText text="End date must be within 31 days of start date" />
              )}
            </td>
            <td>
              <table>
                <tbody>
                  <tr>
                    <th style={{ paddingTop: "5px" }}>Start Date</th>
                    <th style={{ paddingTop: "5px" }}>End Date</th>
                  </tr>
                  <tr>
                    <td>
                      <input
                        style={{ width: "120px", marginRight: "15px", paddingLeft: "5px" }}
                        ref={startDateRef}
                        type="text"
                        required
                        minLength="10"
                        maxLength="10"
                        placeholder="YYYY-MM-DD"
                        onChange={handleDateChange}
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: "120px", paddingLeft: "5px" }}
                        ref={endDateRef}
                        type="text"
                        required
                        minLength="10"
                        maxLength="10"
                        placeholder="YYYY-MM-DD"
                        onChange={handleDateChange}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="2" style={{ paddingTop: "10px" }}>
                      <button
                        className="saveButton download-round-buttons"
                        onClick={doDownload}
                        disabled={!areDatesValid}
                      >
                        {props.roundType === "monitoring"
                          ? "Download Monitoring Data"
                          : "Download Cumulative Inspection Data"}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default DownloadCumulativeReport;
